import React from 'react';
import { X } from 'lucide-react';
import { Button, Chip } from 'common/components';
import { CallType } from 'types/call.type';
import type { Lead } from '../CallDetailsModal/CallDetailsModal';
import { formatCallOutcome, formatLeadStatus, getLeadStatusChipColor, getOutcomeChipColor } from '../../utils';
import { LiveMonitorTranscript } from './LiveMonitorTranscript';

import styles from './LiveMonitorDrawer.module.scss';

export interface LiveMonitorDrawerProps {
    selectedCall: Lead | null;
    onClose: () => void;
}

const getDirectionLabel = (callDirection?: string): string => {
    const value = callDirection?.toUpperCase();
    if (value?.includes('INBOUND')) return 'Inbound';
    if (value?.includes('OUTBOUND')) return 'Outbound';
    if (value?.includes('MANUAL')) return 'Manual';
    return '—';
};

export const LiveMonitorDrawer: React.FC<LiveMonitorDrawerProps> = ({ selectedCall, onClose }) => {
    if (!selectedCall) {
        return (
            <aside className={styles.drawer} data-testid='live-monitor-drawer-empty' aria-label='Call details'>
                <div className={styles.empty}>
                    <h3 className={styles.emptyTitle}>Call details</h3>
                    <p className={styles.emptyCopy}>Select an active call to view details and live transcript.</p>
                </div>
            </aside>
        );
    }

    const isCallInProgress =
        selectedCall.outcome === CallType.IN_PROGRESS ||
        selectedCall.outcome === CallType.TRANSFER_IN_PROGRESS ||
        selectedCall.outcome === CallType.HUMAN_ON_CALL;

    return (
        <aside className={styles.drawer} data-testid='live-monitor-drawer' aria-label='Call details drawer'>
            <header className={styles.header}>
                <div>
                    <h3 className={styles.title}>Call details</h3>
                    <p className={styles.subtitle}>{selectedCall.name}</p>
                </div>
                <Button variant='ghost' size='sm' iconOnly icon={X} aria-label='Close call details' onClick={onClose} />
            </header>

            <div className={styles.detailsGrid}>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Number</span>
                    <span className={styles.detailValue}>{selectedCall.number}</span>
                </div>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Status</span>
                    <Chip variant='filled' corner='pill' color={getOutcomeChipColor(selectedCall.outcome)} size='sm'>
                        {formatCallOutcome(selectedCall.outcome)}
                    </Chip>
                </div>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Started</span>
                    <span className={styles.detailValue}>
                        {selectedCall.callDate} {selectedCall.callTime}
                    </span>
                </div>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Party</span>
                    <Chip
                        variant='filled'
                        corner='pill'
                        color={getLeadStatusChipColor(selectedCall.crmStatus)}
                        size='sm'
                    >
                        {formatLeadStatus(selectedCall.crmStatus)}
                    </Chip>
                </div>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Direction</span>
                    <span className={styles.detailValue}>{getDirectionLabel(selectedCall.callDirection)}</span>
                </div>
                <div className={styles.detailItem}>
                    <span className={styles.detailLabel}>Duration</span>
                    <span className={styles.detailValue} title={selectedCall.durationTooltip}>
                        {selectedCall.duration}
                    </span>
                </div>
            </div>

            <div className={styles.transcriptSection}>
                <h4 className={styles.transcriptTitle}>Live transcript</h4>
                <LiveMonitorTranscript callId={selectedCall.callId} isCallInProgress={isCallInProgress} />
            </div>
        </aside>
    );
};
